import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/adminApi";
import { formatMoney, formatCents } from "../../lib/pricing";
import { isoToEtLongDate, isoToEtTime, prettyTime } from "../../lib/time";
import StatusBadge from "../../components/admin/StatusBadge";

function balanceLabel(b) {
  const status = b.booking_status;
  if (status !== "confirmed") return null;
  const balancePaid = Number(b.balance_paid_cents || 0);
  const balanceDue = Number(b.balance_due_cents || 0);
  if (balanceDue === 0) return null; // no balance to collect (unusual — deposit >= total)
  const outstanding = Math.max(0, balanceDue - balancePaid);
  const collected = balancePaid > 0 && outstanding === 0;
  if (collected) return { label: "Fully paid", key: "fully_paid" };
  const past = new Date(b.start_datetime).getTime() < Date.now();
  if (past) return { label: `Overdue · ${formatCents(outstanding)}`, key: "balance_overdue" };
  return { label: `Deposit only · ${formatCents(outstanding)} due`, key: "deposit_only" };
}

const STATUSES = [
  { v: "", label: "All statuses" },
  { v: "confirmed", label: "Confirmed" },
  { v: "pending_payment", label: "Pending payment" },
  { v: "cancelled", label: "Cancelled" },
];

export default function AdminBookingList() {
  const [yachts, setYachts] = useState([]);
  const [status, setStatus] = useState("");
  const [yachtId, setYachtId] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .get("/admin/yachts", { params: { include_archived: true } })
      .then((r) => setYachts(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: 50 };
    if (status) params.status = status;
    if (yachtId) params.yacht_id = yachtId;
    if (search) params.search = search;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    adminApi
      .get("/admin/bookings", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, page_size: 50 }))
      .finally(() => setLoading(false));
  }, [status, yachtId, search, fromDate, toDate, page]);

  const items = data.items || [];

  return (
    <div data-testid="admin-booking-list">
      <div>
        <div className="hbe-eyebrow">Reservations</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Bookings</h1>
      </div>

      <div
        className="mt-6 grid gap-3 md:grid-cols-5 hbe-card p-4"
        data-testid="bl-filters"
      >
        <select
          className="hbe-input"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid="bl-filter-status"
        >
          {STATUSES.map((s) => (
            <option key={s.v} value={s.v}>{s.label}</option>
          ))}
        </select>
        <select
          className="hbe-input"
          value={yachtId}
          onChange={(e) => { setYachtId(e.target.value); setPage(1); }}
          data-testid="bl-filter-yacht"
        >
          <option value="">All yachts</option>
          {yachts.map((y) => (
            <option key={y.id} value={y.id}>{y.name || y.slug}</option>
          ))}
        </select>
        <input
          type="date"
          className="hbe-input"
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
          data-testid="bl-filter-from"
        />
        <input
          type="date"
          className="hbe-input"
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(1); }}
          data-testid="bl-filter-to"
        />
        <input
          className="hbe-input"
          placeholder="Search name / email / conf#"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          data-testid="bl-filter-search"
        />
      </div>

      {loading ? (
        <div className="mt-6 text-hbe-ink/50">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 hbe-card p-6 text-hbe-ink/60" data-testid="bl-empty">
          No bookings match these filters.
        </div>
      ) : (
        <>
          <div className="mt-6 overflow-x-auto hbe-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-hbe-ink/55 border-b border-hbe-line">
                  <th className="px-4 py-3">Confirmation</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Date · ET</th>
                  <th className="px-4 py-3">Guests</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Balance</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => {
                  const bal = balanceLabel(b);
                  return (
                    <tr
                      key={b.id}
                      className="border-b border-hbe-line/50 last:border-0 hover:bg-hbe-soft/30"
                      data-testid={`bl-row-${b.confirmation_number}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-hbe-primary">
                        {b.confirmation_number}
                      </td>
                      <td className="px-4 py-3">{b.customer_name}</td>
                      <td className="px-4 py-3">
                        {isoToEtLongDate(b.start_datetime)} · {prettyTime(isoToEtTime(b.start_datetime))}
                      </td>
                      <td className="px-4 py-3">{b.guest_count}</td>
                      <td className="px-4 py-3">{formatMoney(b.price_total)}</td>
                      <td className="px-4 py-3"><StatusBadge status={b.booking_status} /></td>
                      <td className="px-4 py-3" data-testid={`bl-balance-${b.confirmation_number}`}>
                        {bal ? <StatusBadge status={bal.key} /> : <span className="text-hbe-ink/40 text-xs">—</span>}
                        {bal && (
                          <div className="text-[11px] text-hbe-ink/55 mt-0.5">{bal.label.replace(/^(Fully paid|Overdue · |Deposit only · )/,"$1").replace("Fully paid","")}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/bookings/${b.id}`}
                          className="text-sm text-hbe-primary hover:underline"
                        >
                          Detail
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-hbe-ink/55">
            <div>{data.total} total</div>
            <div className="flex gap-2">
              <button
                className="hbe-btn-ghost text-xs px-4 py-2 min-h-0"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="bl-prev"
              >Prev</button>
              <button
                className="hbe-btn-ghost text-xs px-4 py-2 min-h-0"
                disabled={items.length < data.page_size}
                onClick={() => setPage((p) => p + 1)}
                data-testid="bl-next"
              >Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
